import { Inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { BaseEdpPayload } from '@records/controls/services/update-record.model';
import { RecordsUserPermission } from '@records/models/records-user-permission.model';
import { DataObject } from '@shared/models/data-object.model';
import { EntityType } from '@shared/models/entity-enum';
import { IUser } from '@shared/models/iuser.model';
import { UserPermission } from '@shared/models/user-permissions.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RecordEditableCheckerMap } from './user-record-permission.helper';

/**
 * Decorator for UserPermission service that encapsulates and decorates with
 * isRecordEditable and canPublishListing (in records domain, those actions belongs only
 * to records module)
 *
 * TODO: TECH DEBT (Elias or anyone): remove canPublishListing from UserInfoResolver since it is an interface
 * that is not applicable for reports or any other module
 */
@Injectable({ providedIn: 'root' })
export class RecordsUserPermissionService extends RecordsUserPermission {
  constructor(@Inject(UserInfoResolver) private userPermission: UserPermission) {
    super();
  }

  /**
   * Resolves IUser information
   * @returns
   */
  resolve(): IUser {
    return this.userPermission.resolve();
  }

  /**
   * Whether if current user has EDP Write access
   * @returns
   */
  hasEDPWriteAccess(): Observable<boolean> | Promise<boolean> {
    return this.userPermission.hasEDPWriteAccess();
  }

  /**
   * Whether if user has trusted user role
   * @returns
   */
  isTrustedUser(): Observable<boolean> | Promise<boolean> {
    return this.userPermission.isTrustedUser();
  }

  /**
   * Whether if user has/belongs given group
   * @param groupName
   * @returns
   */
  hasPermission(groupName: string): Observable<boolean> | Promise<boolean> {
    return this.userPermission.hasPermission(groupName);
  }

  /**
   * Verifies that a given record is able to be edited based on entity or record information
   * @param entity
   * @param record
   * @returns
   */
  isRecordEditable(entity: EntityType, record: DataObject & BaseEdpPayload): Observable<boolean> {
    return from(this.userPermission.hasEDPWriteAccess()).pipe(
      map(isWriteAllowed => {
        const isRecordEditableAssert = RecordEditableCheckerMap[entity];
        const isRecordEditable = isRecordEditableAssert
          ? isRecordEditableAssert(record)
          : /* is editable if no checker callback found */ true;
        return isWriteAllowed && isRecordEditable;
      })
    );
  }

  /**
   * Can publish listing
   * @returns
   */
  public canPublishListings() {
    return from(this.hasPermission(environment.appConstants.PUBLISH_LISTINGS_GROUP_NAME));
  }
}
